import React, { useEffect, useState } from "react";
import "./metrics.css";
import { useLazyQuery } from "@apollo/client";
import { GET_VISITS } from "../../../../utils/GraphQL/queries";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrSectionPanel from "../../../../components/BdrSectionPanel/BdrSectionPanel";
import AdminBDRSpinner from "../../adminComponents/AdminBDRSpinner/AdminBDRSpinner";
import BusinessMetricsSection from "../../adminComponents/BusinessMetrics/BusinessMetricsSection";
import DoorMarginSection from "../../adminComponents/DoorMargin/DoorMarginSection";

/**
 * Metrics — a tab page on `BdrSectionPanel`.
 *
 * ─────────────────────────────────────────────────────────────────
 * THE TAB STRIP WAS A FLOATING BOX. `.metrics-tabs` painted its own gradient,
 * a 2px white border and a `backdrop-filter`, and its comment said why: "the
 * page background is the door-photo collage, which the bare tab buttons were
 * floating on top of unreadably". That collage is gone, so the box was
 * defending against a background that no longer exists — and it left the tabs
 * hovering above the content they switch rather than attached to it. They sit
 * in the panel header now, like Business Ledger and Image Groups.
 *
 * WHAT WENT WITH IT: `handlePageHeight`, the FIFTH hand-rolled
 * `window.innerHeight - navHeight` measurement in this app and the second
 * broken one. It summed three `offsetHeight` reads and then added a whole
 * extra `window.innerHeight`, leaving roughly a viewport of dead scroll under
 * the content; it `NaN`'d if any of the three selectors missed; its first
 * variable was named `shopInvHeight` while reading `#sign-assign-height-marker`
 * (copy-pasted from Shop Inventory); and its `heightTrigger` was wired only to
 * Sign Assignment, which rendered on the Traffic tab alone — so the Business
 * and Door Margin tabs sat under a dead `minHeight: 2000px` default that
 * nothing ever recalculated. With it went `totalMinHeight`,
 * `pageHeightTrigger`, `metricsPageRef` and `#metrics-hdr-height-marker`.
 *
 * SIGN ASSIGNMENTS MOVED OUT, to Defaults Setup → Signs (admin-only). It is
 * the table that names a yard sign, which is setup; it only lived here because
 * the Signs readout below reads the same table. This page reports; it no
 * longer edits. `getVisitTrigger` went with it — it existed to re-run the
 * traffic query after an edit made on the same page.
 */

/* Each option's value is the window in epoch ms, as a string, which is what
   `getVisits` takes. "0" is the sentinel for "no limit" and is translated to
   `now` by the caller, so the search floor becomes 0. */
const TIME_OPTIONS = [
  { value: "604800000", label: "Week" },
  { value: "1209600000", label: "2 Weeks" },
  { value: "2419200000", label: "Month" },
  { value: "31556926000", label: "Year" },
  { value: "0", label: "Lifetime" },
];

const TABS = [
  { key: "traffic", label: "Site Traffic" },
  { key: "business", label: "Business" },
  { key: "doorMargin", label: "Door Margin" },
];

const Metrics = () => {
  // "traffic" = the pre-existing site-analytics view; "business" = the ledger
  // rollups. The whole route is admin/developer gated (App.js).
  const [activeTab, setActiveTab] = useState("traffic");
  const [getVisits] = useLazyQuery(GET_VISITS);
  const [timeLimit, setTimeLimit] = useState("2419200000");
  const [visits, setVisits] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    getAndSetVisits();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLimit]);

  /*
   * OWNS `loading`. It used to be raised by the select handler instead, which
   * deadlocked the page: `BdrSelectPopup` fires `onChange` on every commit,
   * including re-picking the row that is already selected, so the handler set
   * `loading` true and then set `timeLimit` to the value it already held — no
   * state change, so the effect keyed on `[timeLimit]` never re-ran and
   * nothing ever set `loading` back to false. `AdminBDRSpinner` is
   * `position: fixed` at `z-index: 1500` across the whole viewport, so that
   * stuck spinner blocked the entire app until a reload.
   *
   * With the flag owned here, a no-op re-pick simply does nothing.
   */
  const getAndSetVisits = async () => {
    setLoading(true);

    const time = Date.now().toString();
    let timeLimitChoice = null;
    let conversionRate = "N/A";

    if (timeLimit !== "0") {
      timeLimitChoice = timeLimit;
    }

    /*
     * On @apollo/client 3.8+ (3.10.8 installed; package.json pins ^3.5.8, and
     * `^` cannot resolve downward) `useLazyQuery`'s execute promise RESOLVES on
     * a GraphQL error rather than rejecting, so `data` simply comes back
     * undefined. 3.5–3.7 rejected here instead, which is why this reads the
     * result's `error` rather than relying on a catch. Without it the page
     * cleared its spinner and left the PREVIOUS numbers on screen under the new
     * time window, which is worse than showing nothing.
     */
    const { data, error } = await getVisits({
      variables: {
        current: time,
        timeLimit: timeLimitChoice || time,
      },
    });

    if (error || !data?.getVisits) {
      setLoadError(error?.message || "Could not load visit data.");
      setLoading(false);
      return;
    }

    setLoadError("");

    const uniqueVisits = data?.getVisits?.uniqueVisits;
    const conversions = data?.getVisits?.conversions;

    if (uniqueVisits !== "0" && conversions !== "0") {
      conversionRate = ((conversions / uniqueVisits) * 100).toFixed(2);
    }

    /*
     * `signs` arrives as a JSON string keyed by sign NAME where an assignment
     * exists and by sign NUMBER where none does (resolvers.js, getVisits).
     *
     * Key "0" is dropped here too. The server stamps every visit with
     * `fromSign = signNum || "0"` and comments "sign 0 is assigned to no one",
     * so "0" is the not-from-a-sign sentinel and its count is simply ordinary
     * traffic — already reported, in full, by the Pages column beside this.
     *
     * THE REAL GUARD IS SERVER-SIDE, in `getVisits`, and has to be: the server
     * renames a bucket to its assignee before stringifying, so a `SignAssign`
     * row with `signNum: "0"` would arrive here under a person's NAME and sail
     * straight past a filter that compares keys — reporting the whole site's
     * direct traffic as that one sign's scans. This line only still catches
     * the unnamed case; it is kept because it costs nothing.
     */
    const signsData = Object.entries(
      JSON.parse(data?.getVisits?.signs || "{}")
    ).filter(([assignee]) => assignee !== "0");

    setVisits({ ...data?.getVisits, signs: signsData, conversionRate });
    setLoading(false);
  };

  const handleTimeSelect = (event) => {
    setTimeLimit(event.target.value);
  };

  const signs = visits?.signs || [];

  return (
    <div className="metrics-page">
      <BdrSectionPanel
        className="metrics-panel"
        title="Metrics"
        headerClassName="metrics-hdr"
        headerExtra={
          <div className="metrics-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`metrics-tab${
                  activeTab === tab.key ? " metrics-tab-active" : ""
                }`}
                aria-pressed={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
        bodyClassName="metrics-body"
      >
        {activeTab === "business" && <BusinessMetricsSection />}

        {/* Door margin (inventory hardening Phase 4). Both of these draw their
            own headers and surfaces inside the body — they are whole sections,
            not panels of their own, and the page panel frames them. */}
        {activeTab === "doorMargin" && <DoorMarginSection />}

        {activeTab === "traffic" && (
          <div className="metrics-traffic">
            <div className="metrics-traffic-hdr">
              <h3 className="metrics-sub-hdr">Visit Analytics</h3>
              <BdrInput
                className="metrics-time-select"
                size="sm"
                label="Time parameter"
                name="timeLimit"
                value={timeLimit}
                onChange={handleTimeSelect}
                options={TIME_OPTIONS}
              />
            </div>

            {loading && <AdminBDRSpinner message={"Loading..."} />}

            {!loading && loadError && (
              <div className="bdr-alert bdr-alert--danger bdr-alert--start">
                {loadError}
              </div>
            )}

            {!loading && !loadError && (
              <div className="metrics-cols">
                <div className="metrics-col">
                  <h4 className="metrics-col-hdr">Pages</h4>
                  <ul className="metrics-ul">
                    <li>
                      <span>Unique visits</span>
                      <b>{visits?.uniqueVisits ?? 0}</b>
                    </li>
                    <li>
                      <span>Requested quote</span>
                      <b>{visits?.conversions ?? 0}</b>
                    </li>
                    <li>
                      <span>Quote conversion rate</span>
                      <b>
                        {visits?.conversionRate === "N/A"
                          ? "N/A"
                          : `${visits?.conversionRate}%`}
                      </b>
                    </li>
                    <li>
                      <span>Home page</span>
                      <b>{visits?.home ?? 0}</b>
                    </li>
                    <li>
                      <span>Quote page</span>
                      <b>{visits?.quote ?? 0}</b>
                    </li>
                    <li>
                      <span>Our Doors page</span>
                      <b>{visits?.ourDoors ?? 0}</b>
                    </li>
                    <li>
                      <span>Contact page</span>
                      <b>{visits?.contact ?? 0}</b>
                    </li>
                    <li>
                      <span>About Us page</span>
                      <b>{visits?.about ?? 0}</b>
                    </li>
                  </ul>
                </div>

                <div className="metrics-col">
                  <h4 className="metrics-col-hdr">Signs</h4>

                  {/*
                    An empty state, not a bare heading. With no sign scans in
                    the window this column rendered a title over nothing, which
                    reads exactly like a page that failed to load its data.
                  */}
                  {signs.length === 0 ? (
                    <div className="bdr-empty">
                      No sign scans in this period.
                    </div>
                  ) : (
                    <ul className="metrics-ul">
                      {signs.map(([assignee, count]) => (
                        /*
                          Keyed by the assignee, which is the object key the
                          server built this from and is therefore unique.
                          It was `Date.now() + index` — a key regenerated on
                          every render, so React matched nothing to the
                          previous tree and remounted every row each time.
                        */
                        <li key={assignee}>
                          <span>
                            {/^\d/.test(assignee)
                              ? `Sign ${assignee}`
                              : assignee}
                          </span>
                          <b>{count} scans</b>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </BdrSectionPanel>
    </div>
  );
};

export default Metrics;
